import { Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import type { WardMemberDto } from "~/lib/api";

export interface RemoveMemberDialogProps {
  /** The member to remove, or null when the dialog is closed. */
  member: WardMemberDto | null;
  removing: boolean;
  error?: string | null;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation before removing a member from a ward (HAN-87). Removing only ends
 * their membership of this ward — their account and clinical record are kept — so
 * the copy is matter-of-fact. The API still refuses to remove a ward's last
 * admin (409), surfaced here as an error rather than silently failing.
 */
export function RemoveMemberDialog({
  member,
  removing,
  error = null,
  onConfirm,
  onOpenChange,
}: RemoveMemberDialogProps): JSX.Element {
  return (
    <Dialog
      open={member !== null}
      onOpenChange={(open) => {
        if (!open && !removing) onOpenChange(false);
      }}
    >
      <DialogContent>
        {member && (
          <>
            <DialogHeader>
              <DialogTitle>Remove {member.fullName}?</DialogTitle>
              <DialogDescription>
                They&rsquo;ll lose access to this ward&rsquo;s handovers. Their account stays, and
                you can add them back at any time.
              </DialogDescription>
            </DialogHeader>

            {error ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" disabled={removing} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={removing} onClick={onConfirm}>
                {removing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                Remove member
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
